import { ASSIGNED_SEATING, common } from "@shared/constants";
import { useMemo } from "react";
import {
  SeatingItemPropertyMap,
  SeatingEventPropertyMap,
} from "@shared/modules/seating/types/seating-property-types";
import {
  PublicBaseEventSerializer,
  TicketTypeSerializer,
} from "@shared/modules/events/types/public";
import { VenueBasedEventSerializer } from "@app-contexts/dashboard/features/events/data/types/events";
import {
  eventHasWaitlistItems,
  isSoldOutEvent,
} from "@shared/modules/events/utils/event-utils";
import {
  isOnSaleTicketType,
  isTicketTypeSoldOut,
  isTicketTypeWaitlisted,
} from "@shared/modules/ticket-types/utils/ticket-type-utils";
import { useSeatingState } from "@shared/modules/seating/contexts/useSeatingState";
import { showItemPasswordInput } from "@shared/utils/password/utils";
import { useBasketService } from "@shared/modules/basket/context/BasketServiceContext";
import { SelectionItemType } from "../ItemSelectModal.props";

export function useSelectionItem(props: SelectionItemType) {
  const { seatingServiceState } = useSeatingState();
  const { mapItemType, basketType } = seatingServiceState;
  const {
    venueData,
    seatingEvent,
    itemsOnSeat,
    itemSelectData,
    updateTicketAuthArray,
    selectItemPermission,
    getCurrentSelectedAmount,
    ticketAuthArray,
    item,
    isDistributeGroupSale,
    isCreateGroupSale,
  } = props;

  const { isBasketUpdating, purchaseSourcePlatform, paymentType } =
    useBasketService();

  // Memberships are always on sale
  // TODO: change this if an onsale field is added
  const isDisabledSelector =
    isBasketUpdating ||
    (!isOnSaleTicketType(item, item.bypass_sale_starts_on_box_office) &&
      mapItemType !== common.SeatingMapItemType.MEMBERSHIP);

  const allInPricing = Boolean(venueData.all_in_pricing_is_enabled);

  // do not set items to sold out status if distributing a group sale
  const isSoldOut =
    (item?.usageRemaining === 0 || item?.sold_out) && !isDistributeGroupSale;

  // don't show password form on sold out events, membership maps, or when distributing group sales
  const showPasswordForm =
    !isSoldOut &&
    mapItemType !== common.SeatingMapItemType.MEMBERSHIP &&
    !isDistributeGroupSale;

  const soldOutAndWaitlisted = isSoldOut && isTicketTypeWaitlisted(item);
  const soldOutAndNotWaitlisted = isSoldOut && !isTicketTypeWaitlisted(item);

  // show select buttons if canShowItemSelectButton is true OR memberships are on the map
  const canShowSelectButton =
    canShowItemSelectButton(
      item,
      seatingEvent,
      ticketAuthArray,
      updateTicketAuthArray
    ) ||
    isDistributeGroupSale ||
    mapItemType === common.SeatingMapItemType.MEMBERSHIP;

  /**
   * Returns if quantity buttons are allowed to be shown
   * This is different from the public logic, since user vouchers and dynamic holds are not checked here
   */
  function canShowQuantityButton(
    type: TicketTypeSerializer | SeatingItemPropertyMap,
    inputEvent:
      | PublicBaseEventSerializer
      | VenueBasedEventSerializer
      | SeatingEventPropertyMap
  ) {
    return (
      // Check if ticket is on sale
      isOnSaleTicketType(type, type.bypass_sale_starts_on_box_office) &&
      // AND it's not sold out OR waitlisted
      (!isTicketTypeSoldOut(type) || isTicketTypeWaitlisted(type)) &&
      // AND it's not a sold out event. If the event has waitlisted items,
      // allow adding to cart even if event is sold out
      (!isSoldOutEvent(inputEvent) || eventHasWaitlistItems(inputEvent))
    );
  }

  /**
   * Returns if selection buttons are allowed to be shown on the box office ItemSelectModal
   * This is different from the public logic, since user vouchers and dynamic holds are not checked here
   */
  function canShowItemSelectButton(
    item: TicketTypeSerializer | SeatingItemPropertyMap,
    eventData:
      | PublicBaseEventSerializer
      | VenueBasedEventSerializer
      | SeatingEventPropertyMap,
    authArray: Array<{ id: number; authorized: boolean }>,
    setAuthArray: (input: Array<{ id: number; authorized: boolean }>) => void
  ) {
    return (
      canShowQuantityButton(item, eventData) &&
      !showItemPasswordInput(item, authArray, setAuthArray)
    );
  }

  const minPurchaseLimit = useMemo((): number => {
    if (item.minimum_purchase_limit) {
      return item.minimum_purchase_limit;
    }
    // 1 is the default minimum purchase limit for items
    return item.$countOptions?.[1] ?? common.DEFAULT_MIN_TICKET_PURCHASE_LIMIT;
  }, [item, itemsOnSeat]);

  const maxPurchaseLimit = useMemo((): number => {
    const ticketPermissions = itemsOnSeat?.$permissions;
    const maxItemLimit = item.box_office_purchase_limit ?? item.purchase_limit;

    if (isCreateGroupSale || isDistributeGroupSale) {
      return ticketPermissions?.[0].usage_remaining ?? 1;
    }
    if (maxItemLimit) {
      return maxItemLimit;
    }
    // 8 is the default maximum purchase limit for items
    return (
      item.$countOptions?.[item.$countOptions.length - 1] ??
      common.DEFAULT_MAX_TICKET_PURCHASE_LIMIT
    );
  }, [isCreateGroupSale, isDistributeGroupSale, item, itemsOnSeat]);

  const selectSeatCallback = () =>
    selectItemPermission({
      itemTypeId: item.id,
      permissionId: item.permissionId,
      minPurchaseLimit,
    });

  const selectLocationCallback = (val: number) =>
    selectItemPermission({
      itemTypeId: item.id,
      permissionId: item.permissionId,
      requestAmount: val,
      minPurchaseLimit,
    });

  const currentSelectedAmount = getCurrentSelectedAmount(item);

  const isSeat = itemSelectData?.type === ASSIGNED_SEATING.SEAT.ID;
  const isLocation =
    itemSelectData?.type === ASSIGNED_SEATING.GENERAL_ADMISSION.ID;

  return {
    allInPricing,
    basketType,
    minPurchaseLimit,
    maxPurchaseLimit,
    canShowSelectButton,
    showPasswordForm,
    soldOutAndWaitlisted,
    soldOutAndNotWaitlisted,
    isDisabledSelector,
    paymentType,
    purchaseSourcePlatform,
    currentSelectedAmount,
    isSeat,
    isLocation,
    selectSeatCallback,
    selectLocationCallback,
  };
}
